import { A11yModule } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  inject,
} from '@angular/core';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsScrollComponent } from '../styles/ds-scroll';

let nextAnchoredModalId = 0;

@Component({
  selector: 'ds-anchored-modal',
  standalone: true,
  imports: [A11yModule, DsIconButtonV2Component, DsScrollComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible) {
      <div
        class="ds-anchored-modal__backdrop"
        [style.top.px]="overlayTop"
        aria-hidden="true"
        (click)="onBackdropClick()"
      ></div>

      <section
        #panel
        class="ds-anchored-modal__panel"
        [style.left.px]="panelLeft"
        [style.top.px]="panelTop"
        [style.--ds-anchored-modal-available-height.px]="availableHeight"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="showHeader && title ? titleId : null"
        [attr.aria-label]="showHeader && title ? null : ariaLabel"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
      >
        <svg
          class="ds-anchored-modal__arrow"
          [style.left.px]="arrowLeft"
          viewBox="0 0 28 12"
          fill="none"
          aria-hidden="true"
        >
          <path d="M28 12H4.10134e-06C-0.00745699 12 10.1669 0 14.2471 0C18.3273 0 27.9935 12 28 12Z" />
        </svg>

        @if (showHeader) {
          <header class="ds-anchored-modal__header">
            <h2 [id]="titleId" class="ds-anchored-modal__title">{{ title }}</h2>
            @if (showCloseButton) {
              <ds-icon-button-v2
                icon="Close"
                size="s"
                emphasis="secondary"
                ariaLabel="Cerrar"
                (clicked)="dismiss()"
              />
            }
          </header>
        }

        <div
          dsScroll
          class="ds-anchored-modal__body"
          [class.ds-anchored-modal__body--without-header]="!showHeader"
        >
          <ng-content />
        </div>
      </section>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    .ds-anchored-modal__backdrop {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: var(--ds-anchored-modal-z-index);
      background: var(--ds-anchored-modal-overlay);
      animation: ds-anchored-modal-fade-in var(--ds-anchored-modal-transition-duration) ease-out;
    }

    .ds-anchored-modal__panel {
      position: fixed;
      z-index: var(--ds-anchored-modal-panel-z-index);
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      width: min(
        var(--ds-anchored-modal-responsive-width, var(--ds-anchored-modal-width)),
        calc(100vw - var(--ds-anchored-modal-viewport-margin) - var(--ds-anchored-modal-viewport-margin))
      );
      max-height: min(
        var(--ds-anchored-modal-max-height),
        var(--ds-anchored-modal-available-height)
      );
      border-radius: var(--ds-anchored-modal-border-radius);
      background: var(--ds-anchored-modal-background);
      filter: drop-shadow(
        var(--ds-anchored-modal-shadow-offset-x)
        var(--ds-anchored-modal-shadow-offset-y)
        var(--ds-anchored-modal-shadow-blur)
        var(--ds-anchored-modal-shadow-color)
      );
      color: var(--ds-anchored-modal-title-color);
      overflow: visible;
      animation: ds-anchored-modal-enter var(--ds-anchored-modal-transition-duration) ease-out;
    }

    .ds-anchored-modal__arrow {
      position: absolute;
      top: calc(var(--ds-anchored-modal-arrow-height) * -1);
      width: var(--ds-anchored-modal-arrow-width);
      height: var(--ds-anchored-modal-arrow-height);
      fill: var(--ds-anchored-modal-background);
      pointer-events: none;
    }

    .ds-anchored-modal__header {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      box-sizing: border-box;
      width: 100%;
      padding: var(--ds-anchored-modal-header-padding-y) var(--ds-anchored-modal-header-padding-x);
      gap: var(--ds-anchored-modal-header-gap);
      flex: 0 0 auto;
      border-radius: var(--ds-anchored-modal-border-radius) var(--ds-anchored-modal-border-radius) 0 0;
      background: var(--ds-anchored-modal-background);
      box-shadow: var(--ds-anchored-modal-header-shadow);
    }

    .ds-anchored-modal__title {
      flex: 1 1 0;
      min-width: 0;
      margin: 0;
      color: var(--ds-anchored-modal-title-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-anchored-modal-title-font-size);
      font-weight: var(--ds-anchored-modal-title-font-weight);
      line-height: var(--ds-anchored-modal-title-line-height);
    }

    .ds-anchored-modal__body {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
      min-height: 0;
      padding: var(--ds-anchored-modal-body-padding);
      gap: var(--ds-anchored-modal-body-gap);
      overflow: auto;
      border-radius: 0 0 var(--ds-anchored-modal-border-radius) var(--ds-anchored-modal-border-radius);
      background: var(--ds-anchored-modal-background);
    }

    .ds-anchored-modal__body--without-header {
      border-radius: var(--ds-anchored-modal-border-radius);
    }

    @keyframes ds-anchored-modal-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes ds-anchored-modal-enter {
      from {
        opacity: 0;
        transform: translateY(calc(var(--ds-anchored-modal-anchor-gap) * -1));
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-anchored-modal__backdrop,
      .ds-anchored-modal__panel {
        animation: none;
      }
    }
  `],
})
export class DsAnchoredModalComponent implements OnChanges, OnDestroy {
  @Input({ transform: booleanAttribute }) visible = false;
  @Input() title = '';
  @Input() ariaLabel = 'Modal';
  @Input() anchor: HTMLElement | null = null;
  @Input() overlayBoundary: HTMLElement | null = null;
  @Input({ transform: booleanAttribute }) showHeader = true;
  @Input({ transform: booleanAttribute }) showCloseButton = true;
  @Input({ transform: booleanAttribute }) dismissOnBackdropClick = true;

  @Output() dismissed = new EventEmitter<void>();

  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;

  readonly titleId = `ds-anchored-modal-title-${nextAnchoredModalId++}`;

  panelLeft = 0;
  panelTop = 0;
  arrowLeft = 0;
  overlayTop = 0;
  availableHeight = 0;

  private readonly cdr = inject(ChangeDetectorRef);
  private scheduledFrame?: number;
  private listening = false;
  private resizeObserver?: ResizeObserver;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (this.visible) {
        this.startTracking();
        this.schedulePositionUpdate();
      } else {
        this.stopTracking();
      }
      return;
    }

    if (this.visible && (changes['anchor'] || changes['overlayBoundary'])) {
      this.observeResizeTargets();
      this.schedulePositionUpdate();
    }
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }

  dismiss(): void {
    this.dismissed.emit();
  }

  onBackdropClick(): void {
    if (this.dismissOnBackdropClick) {
      this.dismiss();
    }
  }

  private readonly onViewportChange = (): void => this.schedulePositionUpdate();

  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (this.visible && event.key === 'Escape') {
      event.preventDefault();
      this.dismiss();
    }
  };

  private startTracking(): void {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.addEventListener('scroll', this.onViewportChange, { passive: true, capture: true });
    document.addEventListener('keydown', this.onKeydown);
    this.observeResizeTargets();
  }

  private stopTracking(): void {
    if (this.scheduledFrame !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.scheduledFrame);
      this.scheduledFrame = undefined;
    }

    if (!this.listening || typeof window === 'undefined') return;
    this.listening = false;
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange, true);
    document.removeEventListener('keydown', this.onKeydown);
    this.resizeObserver?.disconnect();
  }

  private observeResizeTargets(): void {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver ??= new ResizeObserver(this.onViewportChange);
    this.resizeObserver.disconnect();

    const targets = new Set([this.anchor, this.overlayBoundary].filter(
      (target): target is HTMLElement => target !== null,
    ));
    targets.forEach((target) => this.resizeObserver?.observe(target));
  }

  private schedulePositionUpdate(): void {
    if (typeof requestAnimationFrame === 'undefined') return;
    if (this.scheduledFrame !== undefined) cancelAnimationFrame(this.scheduledFrame);
    this.scheduledFrame = requestAnimationFrame(() => {
      this.scheduledFrame = undefined;
      this.updatePosition();
    });
  }

  private updatePosition(): void {
    const panel = this.panel?.nativeElement;
    const anchor = this.anchor;
    if (!panel || !anchor || typeof window === 'undefined') return;

    const panelStyle = getComputedStyle(panel);
    const panelWidth = panel.getBoundingClientRect().width;
    const anchorRect = anchor.getBoundingClientRect();
    const boundaryRect = this.overlayBoundary?.getBoundingClientRect() ?? anchorRect;
    const margin = this.cssNumber(panelStyle, '--ds-anchored-modal-viewport-margin');
    const gap = this.cssNumber(panelStyle, '--ds-anchored-modal-anchor-gap');
    const radius = this.cssNumber(panelStyle, '--ds-anchored-modal-border-radius');
    const arrowWidth = this.cssNumber(panelStyle, '--ds-anchored-modal-arrow-width');
    const idealLeft = anchorRect.right - panelWidth;
    const maxLeft = Math.max(margin, window.innerWidth - margin - panelWidth);
    const left = Math.min(Math.max(idealLeft, margin), maxLeft);
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const minArrowLeft = radius;
    const maxArrowLeft = Math.max(minArrowLeft, panelWidth - radius - arrowWidth);

    this.panelLeft = left;
    this.panelTop = boundaryRect.bottom + gap;
    this.overlayTop = boundaryRect.bottom;
    this.availableHeight = Math.max(0, window.innerHeight - this.panelTop - margin);
    this.arrowLeft = Math.min(
      Math.max(anchorCenter - left - arrowWidth / 2, minArrowLeft),
      maxArrowLeft,
    );
    this.cdr.markForCheck();
  }

  private cssNumber(style: CSSStyleDeclaration, property: string): number {
    const value = Number.parseFloat(style.getPropertyValue(property));
    return Number.isFinite(value) ? value : 0;
  }
}
